<template>
  <v-container class="settings-container billing-view">
    <div v-if="loader.billing">
      <!-- BILLING LOADER-->
      <div class="billing-view__loading" v-if="!loader.error">
        <v-progress-circular model-value="50" :size="60" :width="4" indeterminate
          color="brand-color"></v-progress-circular>
      </div>
      <!-- BILLING LOAD ERROR-->
      <div class="billing-view__error" v-else>
        <h3>Whoops, looks like something went wrong. Please click the button to try again.</h3>
        <v-btn variant="elevated" color="danger-color" class="text-none font-weight-bold" prepend-icon="mdi-reload"
          @click="reloadPage()">
          Reload
        </v-btn>
      </div>
    </div>
    <!-- BILLING CONTENT -->
    <div v-else>
      <!-- page heading -->
      <v-row class="mb-4">
        <v-col cols="12">
          <h1 class="page-title mb-1">Billing</h1>
          <p class="body-text text-medium-emphasis mt-2">
            Change your plan, check usage, and download invoices.
          </p>
        </v-col>
      </v-row>
      <!-- alerts -->
      <v-alert type="info" variant="tonal" icon="mdi-information" class="mb-6" density="compact" color="brand-color"
        rounded="lg" closable>
        Maxed out your account limits? Time to think about an
        <a class="font-weight-bold text-brand-color" @click="$router.push({ name: 'plans.index' })">upgrade</a>.
      </v-alert>
      <!-- limits & usage -->
      <v-row>
        <v-col cols="12" md="4" v-for="card in usageCards" :key="card.title">
          <v-card rounded="lg" class="pa-4" height="110">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <v-avatar rounded="lg" :color="card.color" size="32" class="mr-3">
                  <v-icon :icon="card.icon" color="white" size="18"></v-icon>
                </v-avatar>
                <div>
                  <div class="text-body-medium font-weight-medium text-high-emphasis">
                    {{ card.title }}
                  </div>
                </div>
              </div>
            </div>
            <v-progress-linear :color="card.getUsage() === 'BLOCKED' ? 'red' : card.color" rounded="lg" bg-opacity="0.3"
              height="10" :model-value="getProgressValue(card)" :max="card.getMax() === 'N/A' ? 100 : card.getMax()">
            </v-progress-linear>
            <div class="d-flex align-center justify-space-between mt-2">
              <div class="text-body-small text-medium-emphasis text-start">
                <span v-if="card.getUsage() === 'BLOCKED'" class="text-red">You don't have access to this</span>
                <span v-else-if="card.title === 'Submissions'">
                  <span>{{ card.getUsage() }}</span>
                  <span v-if="card.getMax() !== 'N/A'"> of {{ card.getMax() }}</span>
                  <span> {{ card.unit }} used</span>
                  <v-tooltip location="bottom" width="300">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="13" class="ml-1 cursor-pointer" color="outlined-color">mdi-information</v-icon>
                    </template>
                    <span class="text-tooltip">You have consumed {{ card.getUsage() }} submissions out of
                      which {{ card.getCompleteCount() }} are completed and {{ card.getPartialCount() }} are partial.
                    </span>
                  </v-tooltip>
                </span>
                <span v-else-if="isAdmin && card.title === 'File Uploads'">
                  <v-tooltip location="bottom" text="All admin accounts share 5GB total storage">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props">{{ card.getUsage() }} of {{ card.getMax() }} {{ card.unit }} used</span>
                    </template>
                  </v-tooltip>
                </span>
                <span v-else-if="card.getMax() !== 'N/A'">{{ card.getUsage() }} out of {{ card.getMax() }} {{ card.unit }} used</span>
                <span v-else>{{ card.getUsage() }} {{ card.unit }} used</span>
              </div>
              <div class="text-body-small text-medium-emphasis text-end">
                <span v-if="card.getUsage() === 'BLOCKED'"></span>
                <template v-else-if="card.getResetText() === 'Lifetime Limit'">
                  <v-tooltip location="bottom" text="This means your limit will never reset">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props">{{ card.getResetText() }}</span>
                    </template>
                  </v-tooltip>
                </template>
                <span v-else>{{ card.getResetText() }}</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- invoice data table -->
      <v-card class="section-card elevation-0 rounded-lg mt-6 border-0">
        <!-- HEADER SECTION | title, Current | Previous switch on the right (same switch as the Submissions page) -->
        <div class="card-section-header">
          <div class="d-flex flex-wrap align-center justify-space-between ga-3">
            <h3 class="section-title">Invoices</h3>
            <segmented-toggle v-model="transaction.activeTab" :items="invoiceTabItems" label="Invoices"
              small color="brand-color-dark" :block="$vuetify.display.xs" />
          </div>
        </div>
        <v-window v-model="transaction.activeTab">
          <v-window-item :value="INVOICE_TABS.CURRENT">
            <InvoiceTable :invoices="transaction.billingInvoices" :loading="loader.invoice"
              :has-subscription="transaction.hasBillingSubscription"
              :has-subscription-elsewhere="transaction.hasClassicSubscription" @view-invoice="viewInvoice"
              @view-plans="$router.push({ name: 'plans.index' })" />
          </v-window-item>
          <v-window-item :value="INVOICE_TABS.PREVIOUS">
            <InvoiceTable :invoices="transaction.classicInvoices" :loading="loader.invoice"
              :has-subscription="transaction.hasClassicSubscription"
              :has-subscription-elsewhere="transaction.hasBillingSubscription" @view-invoice="viewInvoice"
              @view-plans="$router.push({ name: 'plans.index' })" />
          </v-window-item>
        </v-window>
      </v-card>

      <!-- snackbar -->
      <v-row>
        <v-col cols="12">
          <v-snackbar rounded="lg" height="50" :color="snackbar.error ? 'error' : 'success'" :timeout="3000"
            v-model="snackbar.show">
            <p class="text-center font-weight-bold mb-0 text-white py-0"> {{ snackbar.text }} </p>
          </v-snackbar>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { toRaw } from 'vue'
import helperMixin from '@/mixins/helper'
import format from '@/mixins/format'
import userMixin from '@/mixins/user'
import pricingMixin from '@/mixins/pricing/index'
import packageBuilderMixin from '@/mixins/package-builder'
import { mapGetters } from 'vuex'
import { usageCount, featureSlugs } from '@/shared/constants/packagebuilder/features'
import { featureProperties } from '../shared/constants/packagebuilder/featureProperties'
import timePeriods from '@/store/time-periods'
import { getLimitations } from '@/api/landing-pages'
import InvoiceTable from '@/components/billing/InvoiceTable.vue'
import SegmentedToggle from '@/components/common/SegmentedToggle.vue'

// Invoices switch: Paddle Billing (current) and Paddle Classic (previous) invoices
const INVOICE_TABS = { CURRENT: 'billing', PREVIOUS: 'classic' }

export default {
  components: { InvoiceTable, SegmentedToggle },
  mixins: [userMixin, helperMixin, packageBuilderMixin, format, pricingMixin],
  data() {
    return {
      loader: {
        billing: true,
        error: false,
        invoice: true
      },
      snackbar: {
        show: false,
        text: null,
        error: false
      },
      INVOICE_TABS,
      invoiceTabItems: [
        { value: INVOICE_TABS.CURRENT, text: 'Current Invoices', icon: 'mdi-invoice' },
        { value: INVOICE_TABS.PREVIOUS, text: 'Previous Invoices', icon: 'mdi-invoice-clock' }
      ],
      transaction: {
        activeTab: INVOICE_TABS.CURRENT,
        billingInvoices: [],
        classicInvoices: [],
        hasBillingSubscription: false,
        hasClassicSubscription: false
      },
      limits: []
    }
  },
  async mounted() {
    try {
      await Promise.all([
        this.packageBuilderMixin_partialLeadsCount(),
        this.packageBuilderMixin_completeLeadCount(),
        this.packageBuilderMixin_formCount(),
        this.packageBuilderMixin_fileUploadStorage()
      ])
    } catch (error) {
      // Continue loading billing data even if one usage metric request fails.
    }

    // Load plan features and transactions in parallel
    await Promise.all([
      this.getPlanFeatures(this.user?.id),
      this.listInvoicesBySource(),
      this.userMixin_checkLpLimitations(getLimitations)
    ])
  },
  computed: {
    featureProperties() {
      return featureProperties
    },
    usageCount() {
      return usageCount
    },
    timePeriods() {
      return timePeriods
    },
    usageCards() {
      return [
        {
          title: 'Forms',
          color: 'brand-color',
          icon: 'mdi-form-select',
          unit: '',
          getUsage: () => {
            if (!this.isAdmin && !this.isSuperCustomer) {
              const formsLimit = this.getPropertyData(featureProperties.NO_OF_FORMS)
              if (formsLimit === 'N/A') return 'BLOCKED'
            }
            return this.formsCount
          },
          getMax: () => {
            if (this.isAdmin || this.isSuperCustomer) {
              const roleLimit = this.getRolePackageLimit(featureProperties.NO_OF_FORMS)
              return roleLimit !== null ? roleLimit : 0
            }
            const formsProperty = this.getPropertyData(featureProperties.NO_OF_FORMS)
            if (formsProperty === 'N/A') return 'N/A'
            const formsLimit = Number.parseInt(formsProperty?.value, 10)
            return Number.isFinite(formsLimit) ? formsLimit : 'N/A'
          },
          getResetText: () => {
            const resetPeriod = this.getResetPeriod(featureProperties.NO_OF_FORMS)
            return resetPeriod === timePeriods.NONE.toLowerCase() ? 'Lifetime Limit' : `Resets ${resetPeriod}`
          }
        },
        {
          title: 'Submissions',
          color: 'success-color',
          icon: 'mdi-list-box',
          unit: '',
          getUsage: () => {
            // For regular customers, check property exists
            if (!this.isAdmin && !this.isSuperCustomer) {
              const property = this.getPropertyData(featureProperties.NO_OF_SUBMISSIONS)
              if (property === 'N/A') return 'BLOCKED'
            }
            const period = this.getUsagePeriod(featureProperties.NO_OF_SUBMISSIONS)
            const completeCount = this.count?.lead?.[period] || 0
            const partialCount = this.count?.partial?.[period] || 0

            return completeCount + partialCount
          },
          getMax: () => {
            // For admin/super customer, use role package limits if available
            if (this.isAdmin || this.isSuperCustomer) {
              const roleLimit = this.getRolePackageLimit(featureProperties.NO_OF_SUBMISSIONS)
              return roleLimit !== null ? roleLimit : 0
            }
            return this.getPropertyData(featureProperties.NO_OF_SUBMISSIONS).value
          },
          getResetText: () => {
            if (this.isAdmin || this.isSuperCustomer) {
              const resetPeriod = this.getResetPeriod(featureProperties.NO_OF_SUBMISSIONS)
              return resetPeriod === timePeriods.NONE.toLowerCase() ? 'Lifetime Limit' : `Resets ${resetPeriod}`
            }
            const resetPeriod = this.getResetPeriod(featureProperties.NO_OF_SUBMISSIONS)
            return resetPeriod === timePeriods.NONE.toLowerCase() ? 'Lifetime Limit' : `Resets ${resetPeriod}`
          },
          getCompleteCount: () => {
            if (!this.isAdmin && !this.isSuperCustomer) {
              const property = this.getPropertyData(featureProperties.NO_OF_SUBMISSIONS)
              if (property === 'N/A') return 0
            }
            const period = this.getUsagePeriod(featureProperties.NO_OF_SUBMISSIONS)
            return this.count?.lead?.[period] || 0
          },
          getPartialCount: () => {
            if (!this.isAdmin && !this.isSuperCustomer) {
              const property = this.getPropertyData(featureProperties.NO_OF_SUBMISSIONS)
              if (property === 'N/A') return 0
            }
            const period = this.getUsagePeriod(featureProperties.NO_OF_SUBMISSIONS)
            return this.count?.partial?.[period] || 0
          }
        },
        {
          title: 'File Uploads',
          color: 'accent-color',
          icon: 'mdi-cloud-upload',
          unit: 'MB',
          getUsage: () => {
            const usage = this.getPropertyUsage(usageCount.FILE_UPLOAD, featureProperties.FILE_UPLOAD_STORAGE)
            // Privileged roles should always have role package limits; keep UI numeric on fallback.
            if ((this.isAdmin || this.isSuperCustomer) && usage === 'N/A') {
              return 0
            }
            return usage === 'N/A' ? 'N/A' : usage
          },
          getMax: () => {
            // For admin/super customer, use role package limits if available
            if (this.isAdmin || this.isSuperCustomer) {
              const roleLimit = this.getRolePackageLimit(featureProperties.FILE_UPLOAD_STORAGE)
              return roleLimit !== null ? roleLimit : 0
            }
            return this.getPropertyData(featureProperties.FILE_UPLOAD_STORAGE).value
          },
          getResetText: () => {
            const resetPeriod = this.getResetPeriod(featureProperties.FILE_UPLOAD_STORAGE)
            return resetPeriod === timePeriods.NONE.toLowerCase() ? 'Lifetime Limit' : `Resets ${resetPeriod}`
          }
        },
        {
          title: 'Lifetime Landing Page Storage',
          color: 'info',
          icon: 'mdi-view-day',
          unit: '',
          getUsage: () => {
            if (!this.isAdmin && !this.isSuperCustomer && !this.userMixin_hasFeatureAccess(featureSlugs.LANDING_PAGE)) {
              return 'BLOCKED'
            }
            return this.landingPageLimitations?.currStorageCount || 0
          },
          getMax: () => {
            if (this.isAdmin || this.isSuperCustomer) {
              const roleLimit = this.getRolePackageLimit(featureProperties.NO_OF_LANDING_PAGES)
              return roleLimit !== null ? roleLimit : 0
            }
            if (!this.userMixin_hasFeatureAccess(featureSlugs.LANDING_PAGE)) {
              return 'N/A'
            }
            return this.landingPageLimitations?.noOfLandingPages || 0
          },
          getResetText: () => 'Lifetime Limit'
        },
        {
          title: 'Landing Page AI Generations',
          color: 'purple',
          icon: 'mdi-robot',
          unit: '',
          getUsage: () => {
            if (!this.isAdmin && !this.isSuperCustomer && !this.userMixin_hasFeatureAccess(featureSlugs.LANDING_PAGE)) {
              return 'BLOCKED'
            }
            return this.landingPageLimitations?.currMonthlyGenerations || 0
          },
          getMax: () => {
            if (this.isAdmin || this.isSuperCustomer) {
              const roleLimit = this.getRolePackageLimit(featureProperties.NO_OF_AI_GENERATED_LANDING_PAGES)
              return roleLimit !== null ? roleLimit : 0
            }
            if (!this.userMixin_hasFeatureAccess(featureSlugs.LANDING_PAGE)) {
              return 'N/A'
            }
            return this.landingPageLimitations?.noOfAiGeneratedLandingPages || 0
          },
          getResetText: () => 'Resets Monthly'
        }
      ]
    },
    ...mapGetters([
      'formsCount'
    ])
  },
  methods: {
    async listInvoicesBySource() {
      try {
        const response = await this.$store.dispatch('subscription/listInvoicesBySource')
        const data = response?.data?.data || {}
        this.transaction.billingInvoices = data.billing?.invoices || []
        this.transaction.classicInvoices = data.classic?.invoices || []
        this.transaction.hasBillingSubscription = Boolean(data.billing?.has_subscription)
        this.transaction.hasClassicSubscription = Boolean(data.classic?.has_subscription)
        this.loader.invoice = false
      } catch (error) {
        this.loader.invoice = false
      }
    },
    viewInvoice(item) {
      if (item?.receipt_url) {
        window.open(item.receipt_url, '_blank')
      } else {
        this.showSnackbar(true, 'OOPS! Unable to fetch invoice.')
      }
    },
    showSnackbar(flag, message) {
      this.snackbar.show = true
      this.snackbar.error = flag
      this.snackbar.text = message
    },
    reloadPage() {
      window.location.reload()
    },
    async getPlanFeatures(id) {
      try {
        const response = await this.planMixin_getPlanFeatures(id)
        const planFeatures = response?.plan?.plan_features || []
        const result = planFeatures.flatMap((feature) =>
          feature?.feature_properties?.map((property) => ({
            title: property?.title,
            reset_period: property?.reset_period?.toUpperCase()?.replace(/\s+/g, '_') ?? '',
            value: property?.value
          })) || []
        )
        this.limits = result
        this.loader.billing = false
      } catch (err) {
        this.loader.error = true
      }
    },
    getPropertyData(propertyName) {
      const foundProperty = this.limits?.find((property) => property.title === propertyName)
      return foundProperty || 'N/A'
    },
    getRolePackageProperty(propertyTitle) {
      if (!Array.isArray(this.user?.role_package_limits)) {
        return null
      }
      return this.user.role_package_limits.find((item) => item?.property_title === propertyTitle) || null
    },
    getRolePackageLimit(propertyTitle) {
      const roleProperty = this.getRolePackageProperty(propertyTitle)
      if (!roleProperty) return null
      const parsed = Number.parseInt(roleProperty.value, 10)
      return Number.isFinite(parsed) ? parsed : null
    },
    getUsagePeriod(propertyTitle) {
      // Privileged roles resolve usage period from role package limits.
      if (this.isAdmin || this.isSuperCustomer) {
        const roleProperty = this.getRolePackageProperty(propertyTitle)
        const rolePeriod = (roleProperty?.reset_period || timePeriods.NONE).toUpperCase()
        return rolePeriod
      }
      // Customers resolve usage period from plan/package builder properties.
      const propertyData = toRaw(this.getPropertyData(propertyTitle))
      const period = (propertyData?.reset_period || timePeriods.NONE).toUpperCase()
      return period
    },
    getResetPeriod(property) {
      // For admin/super customer, check role package limits first
      if (this.isAdmin || this.isSuperCustomer) {
        const roleLimit = this.getRolePackageProperty(property)
        if (roleLimit) {
          const period = (roleLimit.reset_period || timePeriods.NONE).toUpperCase()
          return this.formatMixin_changeCase(period)
        }
      }

      // For regular customers or if role package not found, use plan property data
      const propertyData = toRaw(this.getPropertyData(property))
      const period = (propertyData?.reset_period || timePeriods.NONE).toUpperCase()
      return this.formatMixin_changeCase(period)
    },
    getPropertyUsage(feature, propertyName) {
      const propertyData = toRaw(this.getPropertyData(propertyName))
      const usageProperty = toRaw(this.count?.[feature]) || {}

      // For customers, if property data is undefined, show access denied
      if (!this.isAdmin && !this.isSuperCustomer && propertyData === 'N/A') {
        return 'BLOCKED'
      }

      if (propertyData === 'N/A' && !this.isAdmin && !this.isSuperCustomer) return 'N/A'

      const period = this.getUsagePeriod(propertyName)
      const usageCount = usageProperty?.[period]

      if (this.isAdmin || this.isSuperCustomer) {
        return usageCount !== undefined ? usageCount : 'N/A'
      }

      return usageCount !== undefined ? usageCount : 'N/A'
    },
    getProgressValue(card) {
      if (card.getUsage() === 'BLOCKED') {
        return 0;
      }

      let usage = card.getUsage();
      let max = card.getMax();

      // Handle N/A cases
      if (usage === 'N/A') {
        return 0;
      }

      // If max is unavailable, keep a bounded progress value.
      if (max === 'N/A') {
        return typeof usage === 'number' ? Math.min(usage, 100) : 0;
      }

      // Convert to number if it's a string
      if (typeof usage === 'string') {
        usage = parseInt(usage) || 0;
      }

      // Ensure we return a number
      return typeof usage === 'number' ? usage : 0;
    }
  }
}
</script>
